import { api } from '@/lib/api';

export interface PresignResponse {
  uploadUrl: string;
  key: string;
  publicUrl: string;
  expiresIn: number;
}

export type XhrFactory = () => XMLHttpRequest;

/**
 * Sobe um arquivo direto ao S3: pede a URL assinada à API e faz o PUT pelo
 * navegador (XMLHttpRequest, porque fetch não expõe progresso de envio).
 * O ACL (`public-read`) já vai embutido na query string da URL assinada —
 * o PUT manda só `Content-Type` igual ao `mimeType` do presign; mandar
 * `x-amz-acl` como header de novo faz o S3 responder 403 (HeadersNotSigned).
 * Devolve a URL pública do arquivo.
 */
export async function uploadFileWithPresign(
  file: File,
  folder: string,
  onProgress?: (percent: number) => void,
  xhrFactory: XhrFactory = () => new XMLHttpRequest(),
): Promise<string> {
  const mimeType = file.type || 'application/octet-stream';
  const response = await api.post<{ success: boolean; data: PresignResponse }>('/uploads/presign', {
    fileName: file.name,
    mimeType,
    size: file.size,
    folder,
  });
  const presign = response.data.data;

  await new Promise<void>((resolve, reject) => {
    const xhr = xhrFactory();
    xhr.open('PUT', presign.uploadUrl);
    xhr.setRequestHeader('Content-Type', mimeType);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) onProgress(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Falha no envio (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Falha de rede no envio'));
    xhr.send(file);
  });

  return presign.publicUrl;
}
